import React from 'react';
import { Download, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useResults } from '@/state/useResults';
import { useSession } from '@/state/session';
import { useData } from '@/state/data';
import { useDemoStore } from '@/state/store';
import { can, roleLabel } from '@/domain/roles';
import { fmtChange, fmtNumber, fmtAbsChange } from '@/domain/numbers';
import { formatDateTime, formatRange, formatRangeShort } from '@/domain/time';
import { FIELD_LABELS, PROVENANCE_LABELS, SEVERITY_LABELS, SOURCE_STATUS_LABELS, UNAVAILABLE_SHORT } from '@/lib/labels';
import { machineLabel, presentText } from '@/lib/present';
import { exportDaysCsv, exportMachinesCsv, exportProductionSourceCsv, exportReportCsv, exportSourceDataCsv } from '@/lib/exports';
import { PageSkeleton, Panel, PanelHeader } from '@/components/app/States';
import { Note, SeverityBadge } from '@/components/app/Badges';
import { DefList, PageHeader } from '@/components/app/PageHeader';

function cell(m) {
  if (!m || m.status === 'unavailable' || m.value === null) return <span className="font-sans text-[var(--hr-amber)]">{UNAVAILABLE_SHORT[m?.reason] ?? 'Utilgjengelig'}</span>;
  return (
    <>
      <span className="font-semibold text-[var(--hr-heading)]">{fmtNumber(m.value, m.decimals)}</span> <span className="text-[13px] text-[var(--hr-muted)]">{m.unit}</span>
      {m.status === 'partial' && <span className="ml-1 font-sans text-[13px] text-[var(--hr-amber)]">delvis</span>}
    </>
  );
}

export default function ProduksjonsrapportPage() {
  const r = useResults();
  const { role } = useSession();
  const { sources, dataset, adapterLabel } = useData();
  const store = useDemoStore();
  if (!r.ready) return <PageSkeleton />;
  const { current: c, reference: ref, comparison: cmp, deviations } = r;
  const showCosts = can(role, 'view_costs');
  const rows = cmp.rows.filter((row) => row.key !== 'costPerTonne' || showCosts);
  const findings = deviations.slice(0, 3);
  const machinesById = new Map((dataset?.machines ?? []).map((m) => [m.machineId, m]));

  return (
    <div className="space-y-8">
      <div className="print:hidden">
        <PageHeader eyebrow="Rapporter" title="Perioderapport" lead="Nøkkeltall, sammenligning, tre funn, datadekning og avgrensning – klar for utskrift eller PDF via nettleseren. CSV bruker UTF-8 med BOM, semikolon og norsk desimalformat, og følger rollen din.">
          <Button onClick={() => window.print()} data-testid="print-report-button">
            <Printer className="mr-1.5 h-4 w-4" aria-hidden="true" /> Skriv ut / lagre som PDF
          </Button>
          <Button variant="outline" onClick={() => exportReportCsv(r, role)} data-testid="export-report-csv-button">
            <Download className="mr-1.5 h-4 w-4" aria-hidden="true" /> Rapport (CSV)
          </Button>
        </PageHeader>
      </div>

      <article className="space-y-6 print:space-y-4" data-testid="report-article">
        <Panel className="print:border-0 print:shadow-none">
          <div className="flex flex-col gap-4 px-5 py-6 sm:flex-row sm:items-end sm:justify-between sm:px-6">
            <div className="min-w-0">
              <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[var(--hr-accent-text)]">Hardanger Rock · Dimmelsvik</div>
              <h2 className="font-display mt-1.5 text-[26px] font-semibold leading-tight tracking-tight text-[var(--hr-heading)] sm:text-[30px]">Perioderapport {formatRange(r.fromDate, r.toDate)}</h2>
              <p className="mt-2 text-sm leading-6 text-[var(--hr-muted)]">
                Sammenlignet med {formatRange(r.compare.fromDate, r.compare.toDate)} · Scenario «{r.scenario.name}» · {c.scopeLabel}
                {r.machineIds.length ? ` · Maskin: ${r.machineIds.map(machineLabel).join(', ')}` : ''}
                {r.fractionId ? ` · Fraksjon: ${r.fractionId}` : ''}
              </p>
            </div>
            <dl className="hr-tabular grid shrink-0 grid-cols-2 gap-x-6 gap-y-1 text-[13px] text-[var(--hr-muted)] sm:text-right">
              <dt>Generert</dt>
              <dd className="text-[var(--hr-text)]">{formatDateTime(r.scenario.frozenNow)}</dd>
              <dt>Rolle</dt>
              <dd className="text-[var(--hr-text)]">{roleLabel(role)}</dd>
              <dt>Kilde</dt>
              <dd className="text-[var(--hr-text)]">{adapterLabel} · {presentText(r.scenario.seedVersion)}</dd>
            </dl>
          </div>
        </Panel>

        <Panel className="print:border-0 print:shadow-none" data-testid="report-kpi-panel">
          <PanelHeader title="Nøkkeltall og sammenligning" />
          <div className="hr-scroll overflow-x-auto">
            <Table data-testid="report-kpi-table" className="[&_td]:py-3 [&_th]:h-11">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="pl-5 sm:pl-6">Måltall</TableHead>
                  <TableHead className="text-right">{formatRangeShort(r.fromDate, r.toDate)}</TableHead>
                  <TableHead className="text-right">{formatRangeShort(r.compare.fromDate, r.compare.toDate)}</TableHead>
                  <TableHead className="pr-5 text-right sm:pr-6">Endring</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.key} data-testid={`report-row-${row.key}`}>
                    <TableCell className="pl-5 font-medium sm:pl-6">
                      {row.label}
                      {row.isIllustrative && <span className="ml-1.5 rounded-full bg-[var(--hr-amber-bg)] px-1.5 py-px text-[12px] font-medium text-[var(--hr-amber)]">illustrativ</span>}
                      {row.provenance && row.provenance !== 'reported' && <span className="ml-1.5 rounded-full bg-[var(--hr-inset)] px-1.5 py-px text-[12px] font-medium text-[var(--hr-muted)]" data-testid={`report-provenance-${row.key}`}>{PROVENANCE_LABELS[row.provenance]}</span>}
                    </TableCell>
                    <TableCell className="hr-num text-right">{cell(row.current)}</TableCell>
                    <TableCell className="hr-num text-right">{cell(row.reference)}</TableCell>
                    <TableCell className="hr-num pr-5 text-right sm:pr-6">
                      {row.status === 'ok' ? (
                        <>
                          <span className="font-semibold text-[var(--hr-heading)]">{fmtChange(row, 2)}</span>
                          {!row.isShare && <span className="ml-1.5 text-[13px] text-[var(--hr-muted)]">({fmtAbsChange(row)})</span>}
                        </>
                      ) : (
                        <span className="font-sans text-[var(--hr-muted)]">{row.status === 'cannot' ? 'kan ikke beregnes' : 'sammenligning utilgjengelig'}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {!showCosts && <p className="border-t border-[var(--hr-hairline)] px-5 py-3 text-[13px] text-[var(--hr-muted)] sm:px-6">Kostnadsrader er utelatt for rollen {roleLabel(role)} og tas ikke med i eksport.</p>}
        </Panel>

        <Panel className="print:border-0 print:shadow-none" data-testid="report-co2-panel">
          <PanelHeader title="CO₂-grunnlag" description="Totalt i kg og per produsert tonn (kg/t) beregnes fra samme intervaller som diesel. Rapportert og estimert CO₂ summeres aldri for samme intervall." />
          <div className="grid gap-5 px-5 py-5 sm:px-6 lg:grid-cols-12">
            <div className="grid grid-cols-2 gap-5 lg:col-span-5">
              <div>
                <div className="text-[14px] font-medium text-[var(--hr-muted)]">CO₂ totalt</div>
                <div className="hr-num mt-1 text-[26px] font-semibold leading-none text-[var(--hr-heading)]" data-testid="report-co2-total">{cell(c.co2TotalKg)}</div>
              </div>
              <div>
                <div className="text-[14px] font-medium text-[var(--hr-muted)]">CO₂ per produsert tonn</div>
                <div className="hr-num mt-1 text-[26px] font-semibold leading-none text-[var(--hr-heading)]" data-testid="report-co2-per-tonne">{cell(c.co2PerTonne)}</div>
              </div>
              <div className="col-span-2 text-[13px] leading-5 text-[var(--hr-muted)]">
                Referanse {formatRangeShort(ref.query.fromDate, ref.query.toDate)}: {cell(ref.co2TotalKg)} · {cell(ref.co2PerTonne)}
              </div>
            </div>
            <div className="lg:col-span-7">
              <DefList
                cols={2}
                items={[
                  { label: 'Opphav', value: <span data-testid="report-co2-source">{c.co2.sourceLabel}</span> },
                  { label: 'Intervaller', value: `${c.co2.reportedIntervals} rapportert · ${c.co2.estimatedIntervals} estimert · ${c.co2.missingIntervals} mangler (av ${c.co2.totalIntervals})` },
                  { label: 'Rapportert (telematikk)', value: c.co2.reportedIntervals ? `${fmtNumber(c.co2.reportedKg, 0)} kg` : 'Ingen rapporterte verdier i perioden' },
                  { label: 'Estimert (diesel × faktor)', value: c.co2.estimatedIntervals ? `${fmtNumber(c.co2.estimatedKg, 0)} kg` : 'Ingen estimerte verdier i perioden' },
                  { label: 'Faktor', value: <span data-testid="report-co2-factor">{fmtNumber(c.co2.factor, 2)} {c.co2.factorUnit} – forutsetning, ikke verifisert lokal faktor</span> },
                  { label: 'Metode', value: c.co2.method },
                ]}
              />
              <p className="mt-3 text-[13px] leading-5 text-[var(--hr-muted)]">{c.co2.priorityRule} {c.co2.factorSource}</p>
            </div>
          </div>
        </Panel>

        <Panel className="print:border-0 print:shadow-none" data-testid="report-findings-panel">
          <PanelHeader title="Tre funn" description="Rangert etter alvorlighet. Funn beskriver bidrag og samtidighet, ikke beregnede årsaker." />
          {findings.length === 0 ? (
            <p className="px-5 py-5 text-sm text-[var(--hr-muted)] sm:px-6">Ingen avvik i perioden.</p>
          ) : (
            <ol className="divide-y divide-[var(--hr-hairline)]">
              {findings.map((d, i) => {
                const st = store.statusOf(d.id);
                return (
                  <li key={d.id} className="flex gap-4 px-5 py-4 text-sm sm:px-6">
                    <span className="hr-num mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--hr-inset)] text-sm font-semibold text-[var(--hr-heading)]">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-display font-semibold text-[var(--hr-heading)]">{d.ruleLabel}</span>
                        {d.scope.machineId && <span className="text-[var(--hr-muted)]">· {machineLabel(d.scope.machineId)} {machinesById.get(d.scope.machineId)?.fictionalName ?? ''}</span>}
                        <SeverityBadge severity={d.severity} />
                        <span className="text-[13px] text-[var(--hr-muted)]">Status: {st === 'open' ? 'Åpen' : st === 'reviewing' ? 'Under vurdering' : 'Avklart'}</span>
                      </div>
                      <p className="hr-tabular mt-1 leading-6">{presentText(d.observation)}</p>
                      <p className="mt-0.5 text-[var(--hr-muted)]">Neste kontroll: {presentText(d.nextCheck)}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </Panel>

        <div className="grid gap-6 lg:grid-cols-12 print:grid-cols-2">
          <Panel className="lg:col-span-5 print:border-0 print:shadow-none" data-testid="report-coverage-panel">
            <PanelHeader title="Datadekning" />
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="pl-5 sm:pl-6">Felt</TableHead>
                  <TableHead className="text-right">Valgt periode</TableHead>
                  <TableHead className="pr-5 text-right sm:pr-6">Referanse</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {c.coverage.map((cv, i) => (
                  <TableRow key={cv.field}>
                    <TableCell className="pl-5 sm:pl-6">
                      {FIELD_LABELS[cv.field] ?? cv.field}
                      <div className="text-[12px] leading-4 text-[var(--hr-muted-2)]">{cv.countsLabel}</div>
                    </TableCell>
                    <TableCell className="hr-num text-right">{cv.received}/{cv.expected}{cv.status === 'partial' ? <span className="font-sans text-[13px] text-[var(--hr-amber)]"> (delvis)</span> : ''}</TableCell>
                    <TableCell className="hr-num pr-5 text-right sm:pr-6">{ref.coverage[i]?.received}/{ref.coverage[i]?.expected}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="space-y-1 border-t border-[var(--hr-hairline)] px-5 py-3 text-[13px] text-[var(--hr-muted)] sm:px-6">
              {sources.map((s) => (
                <div key={s.sourceId}>
                  {s.name}: {SOURCE_STATUS_LABELS[s.status]} · {s.modeLabel} · sist vellykket synk {formatDateTime(s.succeededAt)}
                </div>
              ))}
            </div>
          </Panel>
          <Panel className="lg:col-span-7 print:border-0 print:shadow-none" data-testid="report-limitations-panel">
            <PanelHeader title="Avgrensning" />
            <ul className="list-disc space-y-2 px-5 py-5 pl-10 text-sm leading-6 sm:px-6 sm:pl-11">
              <li>Datagrunnlag: eksempeldata ({presentText(r.scenario.seedVersion)}). Ingen tall er Hardanger Rocks faktiske driftstall; se Datakilder og «Om portalen».</li>
              <li>Produsert tonn kommer kun fra produksjonskilden. Lastet og solgt mengde er kontekst og legges aldri til.</li>
              <li>Forholdstall beregnes fra summert teller / summert nevner; manglende påkrevde felt gjør forholdstallet utilgjengelig.</li>
              <li>Kostnad ({c.rates.dieselNokPerLitre} NOK/L, {c.rates.machineNokPerHour} NOK/maskintime) er illustrative forutsetninger, ikke kundens satser og ikke komplett selvkost. CO₂-faktoren {fmtNumber(c.rates.co2KgPerLitre, 2)} {c.rates.co2FactorUnit} er en generisk testforutsetning som må bekreftes før produksjonsbruk; estimatet dekker kun direkte forbrenning og er ikke et sertifisert klimaregnskap.</li>
              <li>Produksjonstid og stopptid kommer fra linjetilstand (PLS) og er et annet måltall enn maskintimer fra telematikk. Mangler linjetilstand, vises «ikke målt» – aldri 0 h.</li>
              <li>Sammenfall i tid mellom tomgang og stopp er dokumentert samtidighet, ikke bevist årsak. Ulik produktmiks og driftsmønster kan påvirke sammenligningen.</li>
              <li>Integrasjoner er lesende adaptere med lokale data/CSV. Ingen kundesystemer er koblet til.</li>
            </ul>
          </Panel>
        </div>
      </article>

      <Panel className="print:hidden" data-testid="report-exports-panel">
        <PanelHeader title="Flere eksporter" description="Synlige aggregerte tabeller og kildedata du har tilgang til. Celler som kan tolkes som formler nøytraliseres." />
        <div className="flex flex-wrap gap-2 px-5 py-5 sm:px-6">
          <Button variant="outline" size="sm" onClick={() => exportDaysCsv(r, role)} data-testid="export-days-csv-button">
            <Download className="mr-1.5 h-4 w-4" aria-hidden="true" /> Dagstabell
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportMachinesCsv(r, role)} data-testid="export-machines-csv-button">
            <Download className="mr-1.5 h-4 w-4" aria-hidden="true" /> Maskintabell
          </Button>
          {can(role, 'export_source_data') && (
            <>
              <Button variant="outline" size="sm" onClick={() => exportSourceDataCsv(r, role, dataset)} data-testid="export-source-machine-csv-button">
                <Download className="mr-1.5 h-4 w-4" aria-hidden="true" /> Kildedata maskin
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportProductionSourceCsv(r, role, dataset)} data-testid="export-source-production-csv-button">
                <Download className="mr-1.5 h-4 w-4" aria-hidden="true" /> Kildedata produksjon
              </Button>
            </>
          )}
        </div>
        <div className="px-5 pb-5 sm:px-6">
          <Note>
            CSV-format: UTF-8 med BOM, semikolon som skilletegn, komma som desimaltegn, enheter i kolonneoverskrift. Metadata (periode, filtre, scenario, genereringstid, rolle og datagrunnlag) ligger som #-linjer øverst. Direkte PDF-knapp tilbys ikke i P0; bruk nettleserens utskrift til PDF.
          </Note>
        </div>
      </Panel>
    </div>
  );
}
